(() => {
  if (customElements.get('wohnjoy-room-products')) return;
  class WohnjoyRoomProducts extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.controller = new AbortController();
      this.observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.load().catch(error => {
          if (error.name !== 'AbortError') console.warn('Wohnjoy: Raumempfehlungen konnten nicht geladen werden.');
        });
      }, { rootMargin: '600px' });
      this.observer.observe(this);
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.controller?.abort();
      cancelAnimationFrame(this.updateFrame);
      this.started = false;
    }
    async load() {
      const config = JSON.parse(this.querySelector('[data-room-config]').textContent);
      const selection = new window.WohnjoyRoomSelection(this.controller.signal);
      const track = this.querySelector('[data-room-track]');
      const content = this.querySelector('[data-room-content]');
      const navigation = this.querySelector('[data-product-navigation]');
      const controls = this.querySelector('[data-product-pages]');
      const previous = this.querySelector('[data-product-previous]');
      const next = this.querySelector('[data-product-next]');
      previous?.setAttribute('aria-controls', track.id);
      next?.setAttribute('aria-controls', track.id);
      let positions = [];
      let active = 0;
      let direction = 1;
      const showActive = () => {
        if (previous) previous.disabled = active === 0;
        if (next) next.disabled = active === positions.length - 1;
        if (!controls) return;
        [...controls.children].forEach((dot, index) => {
          dot.classList.toggle('swiper-pagination-bullet-active', index === active);
          dot.setAttribute('aria-current', index === active ? 'true' : 'false');
        });
        const dot = controls.children[active];
        if (!dot) return;
        const box = controls.getBoundingClientRect(), target = dot.getBoundingClientRect();
        if (target.left < box.left) controls.scrollLeft += target.left - box.left;
        else if (target.right > box.right) controls.scrollLeft += target.right - box.right;
      };
      const go = index => {
        const target = Math.max(0, Math.min(positions.length - 1, index));
        if (!positions.length) return;
        track.scrollTo({ left: direction * positions[target], behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      };
      const update = () => {
        const max = Math.max(0, track.scrollWidth - track.clientWidth);
        direction = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
        if (navigation) navigation.hidden = max < 2;
        const origin = track.children[0]?.getBoundingClientRect().left || 0;
        const nextPositions = [];
        [...track.children].forEach(item => {
          const position = Math.max(0, Math.min(max, direction * (item.getBoundingClientRect().left - origin)));
          if (!nextPositions.length || position - nextPositions[nextPositions.length - 1] > 2) nextPositions.push(position);
        });
        if (nextPositions.length && max > 2) {
          if (max - nextPositions[nextPositions.length - 1] <= 2) nextPositions[nextPositions.length - 1] = max;
          else nextPositions.push(max);
        }
        const changed = nextPositions.length !== positions.length || nextPositions.some((position, index) => Math.abs(position - positions[index]) > .5);
        positions = nextPositions;
        if (changed && controls) {
          const focused = controls.contains(document.activeElement) ? [...controls.children].indexOf(document.activeElement) : -1;
          controls.replaceChildren();
          positions.forEach((position, index) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'swiper-pagination-bullet wj-product-navigation__dot';
            dot.dataset.position = String(index);
            dot.setAttribute('aria-controls', track.id);
            dot.setAttribute('aria-label', (this.dataset.pageLabel || '__NUMBER__').replace('__NUMBER__', index + 1));
            controls.append(dot);
          });
          if (focused >= 0) controls.children[Math.min(focused, positions.length - 1)]?.focus({ preventScroll: true });
        }
        const current = direction * track.scrollLeft;
        active = 0;
        positions.forEach((position, index) => {
          if (Math.abs(position - current) < Math.abs(positions[active] - current)) active = index;
        });
        if (max > 2 && max - current <= 2) active = positions.length - 1;
        showActive();
      };
      const scheduleUpdate = () => {
        cancelAnimationFrame(this.updateFrame);
        this.updateFrame = requestAnimationFrame(update);
      };
      const options = { signal: this.controller.signal };
      previous?.addEventListener('click', () => go(active - 1), options);
      next?.addEventListener('click', () => go(active + 1), options);
      controls?.addEventListener('click', event => {
        const dot = event.target.closest('[data-position]');
        if (dot && controls.contains(dot)) go(Number(dot.dataset.position));
      }, options);
      const keyboard = event => {
        const dot = event.target.closest('[data-position]');
        if (event.target !== track && !dot) return;
        let target = dot ? Number(dot.dataset.position) : active;
        if (event.key === 'ArrowRight') target += direction;
        else if (event.key === 'ArrowLeft') target -= direction;
        else if (event.key === 'Home') target = 0;
        else if (event.key === 'End') target = positions.length - 1;
        else return;
        event.preventDefault();
        target = Math.max(0, Math.min(positions.length - 1, target));
        go(target);
        if (dot) controls.children[target]?.focus({ preventScroll: true });
      };
      controls?.addEventListener('keydown', keyboard, options);
      track.addEventListener('keydown', keyboard, options);
      track.addEventListener('scroll', scheduleUpdate, { passive: true, ...options });
      track.addEventListener('scrollend', update, options);
      this.resizeObserver = new ResizeObserver(scheduleUpdate);
      this.resizeObserver.observe(track);
      const appendCard = (card, category) => {
        const item = document.createElement('div');
        item.className = 'wj-room-products__item';
        item.setAttribute('role', 'listitem');
        item.dataset.category = category.handle;
        const label = document.createElement('a');
        label.className = 'wj-room-products__category';
        label.href = category.url;
        label.textContent = category.title;
        item.append(label, document.importNode(card.content, true));
        track.append(item);
        content.hidden = false;
        update();
      };
      await selection.collect(config, appendCard);
    }
  }
  customElements.define('wohnjoy-room-products', WohnjoyRoomProducts);
})();
